import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { Box, Button, Dialog, DialogContent, Stack, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashDatiProgetto from "./DashDatiProgetto";
import DashOpen from "./DashOpen";

const BASE_API_URL_SUPERSET = 'http://172.16.0.89:8088/api/v1';  // your Superset API base URL
const BASE_URL_SUPERSET = 'http://172.16.0.89:8088';           // your Superset web base URL

type Dashboard = {
  id: string;
  label: string;
  icon: React.ReactElement;
};

const dashboards: Dashboard[] = [
  {
    id: 'fde2efb6-9e6d-49d5-b888-fcaf59911638',
    label: 'Dash Agenti',
    icon: <BarChartIcon fontSize="large" />,
  },
  {
    id: '0cda0da0-f0a0-4355-ba95-1594e5f728cc',
    label: 'Dash Progetti',
    icon: <BarChartIcon fontSize="large" />,
  },
  {
    id: 'a66ff15d-f6b5-4b96-993e-979c5cbc3c67',
    label: 'Dash Proiezione Progetti (TEST)',
    icon: <BarChartIcon fontSize="large" />,
  },
  {
    id: 'dc95c545-f92a-479f-b9ae-76d0966a299a',
    label: 'Dash Proiezione SE (TEST)',
    icon: <BarChartIcon fontSize="large" />,
  },
  {
    id: '0aa56409-f2d7-451c-abe8-0fda43cbc47a',
    label: 'Dash Stato Progetti (DAVID)',
    icon: <BarChartIcon fontSize="large" />,
  },
  {
    id: '55f63af4-b8d9-4420-88e0-6dfcfa4b0a63',
    label: 'Dash Accessi App',
    icon: <BarChartIcon fontSize="large" />,
  },
];

interface EmbedDashboardDialogProps {
  dashboardId: string;
  dashboardLabel: string;
  onClose: () => void;
}

const EmbedDashboardDialog: React.FC<EmbedDashboardDialogProps> = ({ dashboardId, dashboardLabel, onClose }) => {
  const [guestToken, setGuestToken] = useState<string | null>(null);

  useEffect(() => {
    async function fetchGuestToken() {
      try {
        // 1. Login to get access token
        const loginBody = {
          username: 'admin',
          password: 'Super2025@',
          provider: 'db',
          refresh: true,
        };
        const loginResponse = await axios.post(`${BASE_API_URL_SUPERSET}/login`, loginBody, {
          headers: { 'Content-Type': 'application/json' },
        });
        const accessToken = loginResponse.data.access_token;
        if (!accessToken) throw new Error('No access token received');

        // 2. Request guest token
        const guestTokenBody = {
          resources: [{ type: 'dashboard', id: dashboardId }],
          rls: [], // add RLS clauses if needed here
          user: {
            username: 'guest_user',
            first_name: 'Guest',
            last_name: 'User',
          },
        };
        const guestTokenResponse = await axios.post(`${BASE_API_URL_SUPERSET}/guest_token/`, guestTokenBody, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const token = guestTokenResponse.data.token;
        if (!token) throw new Error('No guest token received');

        setGuestToken(token);
      } catch (error) {
        console.error('Error fetching guest token:', error);
      }
    }

    fetchGuestToken();
  }, [dashboardId]);

  useEffect(() => {
    if (guestToken) {
      const mountPoint = document.getElementById('superset-container');
      if (mountPoint) {
        embedDashboard({
          id: dashboardId,
          supersetDomain: BASE_URL_SUPERSET,
          mountPoint,
          fetchGuestToken: async () => guestToken,
          dashboardUiConfig: {
            hideTitle: true,
            filters: { expanded: false },
            urlParams: { standalone: true },
          },
        });

        setTimeout(() => {
          const iframe = document.querySelector('#superset-container iframe') as HTMLIFrameElement | null;
          if (iframe) {
            iframe.style.width = '100%';
            iframe.style.height = 'calc(100vh - 120px)';
            iframe.style.border = 'none';
          }
        }, 500);
      }
    }
  }, [guestToken, dashboardId]);

  return (
    <Dialog onClose={onClose} open fullWidth maxWidth="xl" PaperProps={{ sx: { height: '90vh' } }}>
      <Box display="flex" flexDirection="column" height="100%">
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom="1px solid #ddd">
          <Typography variant="h6" fontWeight="bold" color="primary">
            {dashboardLabel}
          </Typography>
          <Button variant="contained" onClick={onClose} color="error" size="small" startIcon={<CloseIcon />}>
            Chiudi
          </Button>
        </Box>

        <DialogContent sx={{ flexGrow: 1, p: 0 }}>
          <div id="superset-container" style={{ width: '100%', height: '100%' }} />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

const DashboardsList: React.FC = () => {
  const [openDashboardId, setOpenDashboardId] = useState<string | null>(null);
  const [openDashboardLabel, setOpenDashboardLabel] = useState<string | null>(null);

  const openDashboard = (id: string, label: string) => {
    setOpenDashboardId(id);
    setOpenDashboardLabel(label);
  };

  const closeDashboard = () => {
    setOpenDashboardId(null);
    setOpenDashboardLabel(null);

    const container = document.getElementById('superset-container');
    if (container) container.innerHTML = '';
  };

  return (
    <Box sx={{ mt: 8, px: 2 }}>
      {!openDashboardId && (
        <Stack direction="row" spacing={4} justifyContent="center" flexWrap="wrap" mb={4}>
          {dashboards.map((dash) => (
            <Button
              key={dash.id}
              variant="contained"
              onClick={() => openDashboard(dash.id, dash.label)}
              sx={{
                width: 180,
                height: 180,
                fontSize: '1.2rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 3,
                textAlign: 'center',
              }}
            >
              {dash.icon}
              <Box mt={1}>{dash.label}</Box>
            </Button>
          ))}
        </Stack>
      )}



   {openDashboardId && openDashboardLabel && (
        <DashOpen
          dashboardId={openDashboardId}
          dashboardLabel={openDashboardLabel}
          onClose={closeDashboard}
        />
      )}
    </Box>
  );
};

export default DashboardsList;
